import { clearSession, getSessionToken } from '../services/session';
import { createContext, useEffect, useCallback, useContext, useMemo, useState } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(null);

function loadUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch {
    return {};
  }
}

export function AuthProvider({ children }) {
  const [isLogado, setIsLogado] = useState(() => !!getSessionToken());
  const [userData, setUserData] = useState(loadUserData);

  const login = useCallback((user, token) => {
    clearSession();
    localStorage.setItem('auth', 'true');
    localStorage.setItem('userData', JSON.stringify(user));
    if (token) localStorage.setItem('auth_token', token);
    setIsLogado(true);
    setUserData(user);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setIsLogado(false);
    setUserData({});
  }, []);

  useEffect(() => {
    const expire = () => logout();
    const synchronize = (event) => {
      if (event.key && !['auth_token', 'userData'].includes(event.key)) return;
      setIsLogado(!!getSessionToken());
      setUserData(loadUserData());
    };
    window.addEventListener('session-expired', expire);
    window.addEventListener('storage', synchronize);
    const timer = setInterval(() => { if (isLogado && !getSessionToken()) logout(); }, 30000);
    return () => {
      window.removeEventListener('session-expired', expire);
      window.removeEventListener('storage', synchronize);
      clearInterval(timer);
    };
  }, [isLogado, logout]);

  useEffect(() => {
    if (!isLogado) return;
    let active = true;
    const token = getSessionToken();
    api.get('/auth/session').then(({ user }) => {
      if (active && token === getSessionToken()) {
        localStorage.setItem('userData', JSON.stringify(user));
        setUserData(user);
      }
    }).catch(() => { /* O interceptor encerra sessões recusadas; falhas de rede não deslogam. */ });
    return () => { active = false; };
  }, [isLogado]);

  const permissao = useMemo(() => userData.permissao || '', [userData]);
  const equipe = useMemo(() => String(userData.team || '').toLowerCase(), [userData]);
  const isAdministrativo = useMemo(() => permissao === 'administrativo' || equipe === 'administrativo', [permissao, equipe]);

  // Hierarquia: corretor < gerente < administrador < diretor
  const isGerente      = useMemo(() => ['gerente', 'administrador', 'diretor'].includes(permissao) || isAdministrativo, [permissao, isAdministrativo]);
  // Operação administrativa: administrador/RH + administrativo. Desde 11/08/2026 o
  // diretor também entra — ele enxerga e opera tudo que o administrador opera
  // (serviços e gestão). Ver [3.7 - Permissionamento].
  const isAdministrador = useMemo(
    () => permissao === 'administrador' || permissao === 'diretor' || isAdministrativo,
    [permissao, isAdministrativo]
  );
  // Capacidade executiva/multi-equipe: diretor e administrativo.
  const isDiretor      = useMemo(() => permissao === 'diretor' || isAdministrativo, [permissao, isAdministrativo]);
  const isAssistente   = useMemo(() => permissao === 'assistente', [permissao]);

  // Mantém isAdmin como alias de isGerente para compatibilidade interna
  const isAdmin = isGerente;

  const nomeUsuario = useMemo(
    () => userData.nome || userData.name || userData.nomeCorretor || userData.usuario || 'Usuário',
    [userData]
  );

  const idCorretor = useMemo(
    () => userData.id_usuarios || userData.id_corretor || 'Não informado',
    [userData]
  );

  const value = useMemo(
    () => ({ isLogado, userData, permissao, isAdmin, isGerente, isAdministrador, isDiretor, isAssistente, isAdministrativo, nomeUsuario, idCorretor, login, logout }),
    [isLogado, userData, permissao, isAdmin, isGerente, isAdministrador, isDiretor, isAssistente, isAdministrativo, nomeUsuario, idCorretor, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
